import { useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import type {
    APIProductModel,
    ChainedQueueTasksRequestBody,
    DocumentQueueTasks,
    PaginatedRequestQueryParams,
    RegularQueueTaskRequestBody,
} from '@visorpro/client'

import { DocumentsTable } from '../../../../components/documents-table'
import type { Stores } from '../../../../util/store'
import { useDataSets } from '../../../../stores/data-sets-store'
import { useModels } from '../../../../stores/models-store'
import {
    useDocuments,
    type APIUpdateDocumentRequest,
} from '../../../../stores/documents-store'
import { useManufacturers } from '../../../../stores/manufacturers-store'
import { useCategories } from '../../../../stores/categories-store'
import type {
    CREATE_NEW_MODEL,
    IGNORE_MODEL,
    ColumnMapping,
} from '../../../../types'

export interface DataSetDocumentsProps {
    stores: Stores
}

export const DataSetDocuments = observer(
    ({ stores }: DataSetDocumentsProps) => {
        const { dataSetId } = useParams()
        if (!dataSetId) return null

        const [pagination, setPagination] =
            useState<PaginatedRequestQueryParams>({
                offset: 0,
                limit: 1000,
            })
        const [filters, setFilters] = useState({})
        const [sorting, setSorting] = useState<string[]>([])

        const options = useMemo(() => {
            return {
                ...pagination,
                ...filters,
                sort_by: sorting,
                data_set_ids: [dataSetId],
            }
        }, [pagination, filters, sorting, dataSetId])

        const dataSets = useDataSets(stores)
        const models = useModels(stores)
        const documentsStore = useMemo(
            () => stores.dataSets.documentsStoresById[dataSetId],
            [stores, dataSetId],
        )
        const documents = useDocuments(documentsStore, options)

        const manufactures = useManufacturers(stores)
        const categories = useCategories(stores)

        const setEnabled = useCallback(
            (documentId: string, isEnabled: boolean) => {
                void documentsStore.setDocumentEnabled(documentId, isEnabled)
            },
            [documentsStore],
        )

        const updateDocument = useCallback(
            async (documentId: string, body: APIUpdateDocumentRequest) => {
                await documentsStore.update(documentId, body)
            },
            [documentsStore],
        )

        const deleteDocument = useCallback(
            async (documentId: string) => {
                await documentsStore.delete(documentId)
            },
            [documentsStore],
        )

        const setModels = useCallback(
            async (
                documentId: string,
                addedModelIds: string[],
                removedModelIds: string[],
            ) => {
                await Promise.all([
                    documentsStore.addDocumentToModels(
                        documentId,
                        addedModelIds,
                    ),
                    documentsStore.removeDocumentFromModels(
                        documentId,
                        removedModelIds,
                    ),
                ])
            },
            [documentsStore],
        )

        const setDataSets = useCallback(
            async (
                documentId: string,
                addedDataSetIds: string[],
                removedDataSetIds: string[],
            ) => {
                await Promise.all([
                    documentsStore.removeDocumentFromDataSets(
                        documentId,
                        removedDataSetIds,
                    ),
                    documentsStore.addDocumentToDataSets(
                        documentId,
                        addedDataSetIds,
                    ),
                ])
            },
            [documentsStore],
        )

        const createQueueTask = useCallback(
            async (
                task: DocumentQueueTasks | DocumentQueueTasks[],
                documentIds: string[],
            ) => {
                if (!Array.isArray(task)) {
                    const requestData: RegularQueueTaskRequestBody[] = []
                    for (const documentId of documentIds) {
                        requestData.push({
                            kind: task as any,
                            metadata: {
                                document_id: documentId,
                            },
                        })
                    }
                    await stores.queueTasks.create(requestData)
                } else {
                    const requestData: ChainedQueueTasksRequestBody[] = []
                    for (const documentId of documentIds) {
                        requestData.push({
                            chain: [
                                ...task.map((t) => ({
                                    kind: t as any,
                                    metadata: {
                                        document_id: documentId,
                                    },
                                })),
                            ],
                        })
                    }
                    await stores.queueTasks.create(requestData)
                }
            },
            [stores.queueTasks],
        )

        const uploadDocument = async (files: FileList) => {
            await stores.documentUploads.enqueueDocumentUploads(
                dataSetId,
                files,
            )
        }

        const uploadMetadata = async (file: File) => {
            await stores.documentMetadata.uploadMetadataSheet(file)
        }

        const setColumnMapping = useCallback(
            async (
                columnMapping: ColumnMapping,
                manufactuereId: string,
                categoryId: string,
            ) => {
                stores.documentMetadata.setColumnMapping(columnMapping)
                stores.documentMetadata.setProductManufacturerId(manufactuereId)
                stores.documentMetadata.setProductCategoryId(categoryId)
            },
            [stores.documentMetadata],
        )

        const generateProductModelMatchings = useCallback(async () => {
            await stores.documentMetadata.generateProductModelMatchings()
        }, [stores.documentMetadata])

        const changeModelMatching = useCallback(
            (
                modelName: string,
                selected:
                    | Fuzzysort.KeyResult<APIProductModel>
                    | typeof CREATE_NEW_MODEL
                    | typeof IGNORE_MODEL,
            ) => {
                stores.documentMetadata.changeModelMatching(modelName, selected)
            },
            [stores.documentMetadata],
        )

        const submitModelMatching = useCallback(async () => {
            await stores.documentMetadata.submitModelMatching()
        }, [stores.documentMetadata])

        const generateDocumentUpdates = useCallback(async () => {
            await stores.documentMetadata.generateDocumentUpdates(dataSetId)
        }, [dataSetId, stores.documentMetadata])

        const updateDocuments = useCallback(async () => {
            await stores.documentMetadata.updateDocuments(dataSetId)
        }, [dataSetId, stores.documentMetadata])

        const onMatchModeChange = useCallback(async (mode: 'sha512_hash' | 'original_filename') => {
            stores.documentMetadata.setMatchMode(mode)
            await stores.documentMetadata.generateDocumentUpdates(dataSetId)
        }, [dataSetId, stores.documentMetadata])

        return (
            <DocumentsTable
                documents={documents}
                dataSets={dataSets}
                models={models}
                documentUploads={stores.documentUploads.getDataSetUploads(
                    dataSetId,
                )}
                isLoading={documentsStore.isFetching}
                showProgressBars={
                    documentsStore.isUpdating ||
                    stores.documentMetadata.isProcessing ||
                    stores.queueTasks.isCreating
                }
                rowCount={documentsStore.totalDocuments}
                updateDocumentEnabled={setEnabled}
                updateDocument={updateDocument}
                deleteDocument={deleteDocument}
                uploadMetadata={uploadMetadata}
                metadataDialog={{
                    manufactures,
                    categories,
                    csv: stores.documentMetadata.csv,
                    modelMatchings:
                        stores.documentMetadata.productModelMatchings,
                    documentUpdates: stores.documentMetadata.documentUpdates,
                    isProcessing: stores.documentMetadata.isProcessing,
                    matchMode: stores.documentMetadata.matchMode,
                    onMatchModeChange,
                    generateProductModelMatchings,
                    setColumnMapping,
                    changeModelMatching,
                    submitModelMatching,
                    generateDocumentUpdates,
                    updateDocuments,
                }}
                setModels={setModels}
                setDataSets={setDataSets}
                pagination={pagination}
                setPagination={setPagination}
                filters={filters}
                setFilters={setFilters}
                sorting={sorting}
                setSorting={setSorting}
                uploadDocument={uploadDocument}
                createQueueTask={createQueueTask}
            />
        )
    },
)
